import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { SvcReactionService } from '../svc-reaction.service';
import { UserService } from 'projects/lib-shared-core/src/public-api';
import { SvcAppSettings } from 'projects/lib-shared-common/src/public-api';
import { Subject, Subscription, catchError, finalize, of, take, takeUntil, tap } from 'rxjs';
import { Reacted } from '../models/reacted.model';
import { ReactionType } from '../models/reaction-type.model';
import { SvcReactionComponent } from '../svc-reaction.component';
import { ModalTotalReactionsComponent } from '../modal/modal-total-reactions/modal-total-reactions.component';
import { SvcDialogService } from '@solvace/component/svc-dialogs';

@Component({
  selector: 'svc-total-reactions',
  templateUrl: './svc-total-reactions.component.html',
  styleUrls: ['./svc-total-reactions.component.scss'],
})
export class SvcTotalReactionsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public registryUniqueId: number | string;
  @Input() public reaction: SvcReactionComponent;
  @Input() public applicationId: string;
  @Input() public mockMode: boolean = false;
  @Input() public disabled: boolean = false;
  @Input() public classText = 'text-primary';
  @Input() public siteId: number;
  @Input() buttonId: string;

  private _unsubscribeAll = new Subject<void>();

  public loading = true;
  public firstLoading = true;
  public reactionsType: ReactionType[] = [];
  public reactionsTypeReacted: ReactionType[] = [];
  public totalReactions: Reacted[] = [];
  public total: number = 0;
  private _requestSubscription: Subscription;
  private _reactionSubscription: Subscription;

  private get loggedUserId(): string {
    return this._userService.userId$;
  }
  private get loggedUserName(): string {
    return this._userService.user?.fullName;
  }
  private get loggedUserColor(): string {
    return this._userService.user?.initialsColor;
  }
  private get loggedUserPicture(): string {
    return this._userService.user?.pictureFile;
  }
  private get loggedUserRole(): string {
    return this._userService.user?.employeePositionName;
  }

  constructor(
    private _appSettings: SvcAppSettings,
    private _service: SvcReactionService,
    private _dialog: SvcDialogService,
    private _userService: UserService,
    private _cdr: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this.loading = true;
    this._service
      .getReactionsType()
      .pipe(
        takeUntil(this._unsubscribeAll),
        tap((list) => {
          this.reactionsType = list.filter((x) => x.active);
          if (!this.mockMode) {
            this._getTotalReactions();
          } else {
            this.reactionsTypeReacted = this.reactionsType;
            this.total = 160;
            this.loading = false;
            this.firstLoading = false;
            this._cdr.markForCheck();
          }
        }),
        catchError(() => {
          this.loading = false;
          this.firstLoading = false;
          this._cdr.markForCheck();
          return of([]);
        })
      )
      .subscribe(() => {
        this.firstLoading = false;
        this._cdr.markForCheck();
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('reaction' in changes) {
      this._listenExternalReaction();
    }

    const registryChange = changes['registryUniqueId'];
    if (registryChange && !registryChange.firstChange && this._hasValidRegistryUniqueId()) {
      this._getTotalReactions();
    }
  }

  public refresh(): void {
    if (!this.loading) {
      this._service
        .getReactionsType()
        .pipe(
          takeUntil(this._unsubscribeAll),
          tap((list) => {
            this.reactionsType = list.filter((x) => x.active);
            if (!this.mockMode) {
              this._reactionSubscription?.unsubscribe();
              this._reactionSubscription = this.reaction?.onReactionChange
                .pipe(
                  tap(() => {
                    this._getTotalReactions();
                  })
                )
                .subscribe();
              this._getTotalReactions();
            } else {
              this.reactionsTypeReacted = this.reactionsType;
              this.total = 160;
              this.loading = false;
              this._cdr.markForCheck();
            }
          }),
          catchError(() => {
            this.loading = false;
            this._cdr.markForCheck();
            return of([]);
          })
        )
        .subscribe();
    }
  }

  public icreaseTotal(): void {
    this.total++;
    this._cdr.markForCheck();
  }

  public decremenTotal(): void {
    this.total--;
    this._cdr.markForCheck();
  }

  public modalShowReactions(totalReactions: Reacted[]): void {
    if (!this.mockMode && !this.disabled) {
      this._dialog.open(ModalTotalReactionsComponent, { data: totalReactions, width: '420px' });
    }
  }

  private _listenExternalReaction(): void {
    this._reactionSubscription?.unsubscribe();
    if (!this.mockMode && this.reaction) {
      this._reactionSubscription = this.reaction?.onReactionChange
        .pipe(
          takeUntil(this._unsubscribeAll),
          tap((event) => {
            if (!event.anticipated) {
              this._getTotalReactions();
            } else {
              this._requestSubscription?.unsubscribe();
              this._requestSubscription = null;
              this._applyFakeReaction(event.anticipated.currentReactionType, event.anticipated.targetReactionType);
              event.anticipated.onReactionDone$
                .pipe(
                  take(1),
                  takeUntil(this._unsubscribeAll),
                  tap(() => this._getTotalReactions())
                )
                .subscribe();
            }
          })
        )
        .subscribe();
    } else {
      this._reactionSubscription = null;
    }
  }

  private _hasValidRegistryUniqueId(): boolean {
    const value = this.registryUniqueId;
    if (value === null || value === undefined || value === '') return false;
    if (typeof value === 'number') return Number.isFinite(value) && value > 0;
    return true;
  }

  private _getTotalReactions(): void {
    if (!this._hasValidRegistryUniqueId()) {
      this.loading = false;
      this.firstLoading = false;
      return;
    }

    // The counters API rejects any siteId other than the user's current site (400).
    // Posts from a different site than the one the user is currently on can never
    // have their reaction counts fetched, so skip the doomed request up front instead
    // of retrying it (and throwing) on every polling cycle.
    if (this.siteId != null && this.siteId !== this._userService.user.lastSiteId) {
      this.loading = false;
      this.firstLoading = false;
      return;
    }

    if (!this._requestSubscription) {
      if (this.firstLoading) {
        this.loading = true;
      }

      const currentRequest =
        typeof this.registryUniqueId == 'number'
          ? this._service.getIdTotalReaction({
              applicationId: this.applicationId ?? this._appSettings.applicationId,
              registryUniqueId: this.registryUniqueId,
              siteId: this.siteId ?? this._userService.user.lastSiteId,
            })
          : this._service.getUidTotalReaction({
              applicationId: this.applicationId ?? this._appSettings.applicationId,
              registryUniqueUId: this.registryUniqueId,
              siteId: this.siteId ?? this._userService.user.lastSiteId,
            });

      this._requestSubscription = currentRequest
        .pipe(
          takeUntil(this._unsubscribeAll),
          catchError(() => of([] as Reacted[])),
          tap((totalReactions?: Reacted[]) => {
            this.totalReactions = totalReactions;

            this.reactionsTypeReacted = this.reactionsType.filter((x) => (totalReactions.find((y) => y.enumKey == x.enumKey)?.count ?? 0) > 0);
            this.total = totalReactions.reduce((prev, curr) => prev + curr.count, 0);
            this._cdr.markForCheck();
          }),
          finalize(() => {
            this.loading = false;
            this._requestSubscription = null;
            this._cdr.markForCheck();
          })
        )
        .subscribe();
    } else {
      this.loading = !this._requestSubscription.closed;
    }
  }

  private _applyFakeReaction(currentReactionType: ReactionType, targetReactionType: ReactionType): void {
    if (currentReactionType) {
      const totalReaction = this.totalReactions.find((tr) => tr.userHasReacted && tr.enumKey === currentReactionType.enumKey);
      if (totalReaction) {
        totalReaction.userHasReacted = false;
        totalReaction.count--;
        totalReaction.users = totalReaction.users.filter((u) => u.userId !== this.loggedUserId);
      }
    }
    if (targetReactionType) {
      const totalReaction = this.totalReactions.find((tr) => tr.enumKey === targetReactionType.enumKey);
      const user = {
        userId: this.loggedUserId,
        firstLastName: this.loggedUserName,
        preferenceColor: this.loggedUserColor,
        pictureUrl: this.loggedUserPicture,
        role: this.loggedUserRole,
      };
      if (totalReaction) {
        totalReaction.count++;
        totalReaction.userHasReacted = true;
        totalReaction.users = [...totalReaction.users, user];
      } else {
        this.totalReactions.push({
          ...targetReactionType,
          count: 1,
          userHasReacted: true,
          users: [user],
        });
      }
    }

    this.totalReactions = this.totalReactions.map((tr) => ({
      ...tr,
      count: Math.max(tr.count, 0),
    }));
    this.reactionsTypeReacted = this.reactionsType.filter((x) => (this.totalReactions.find((y) => y.enumKey == x.enumKey)?.count ?? 0) > 0);
    this.total = this.totalReactions.reduce((prev, curr) => prev + curr.count, 0);
    this._cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this._reactionSubscription?.unsubscribe();
    this._requestSubscription?.unsubscribe();
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }
}
